/**
 * Browser Prompt Store
 *
 * Tracks whether a browser-level permission prompt is currently on screen — the
 * GeoComply SDK's location explainer dialog ("When prompted, select Allow…"), the
 * native geolocation prompt it triggers, or the push notification prompt.
 *
 * These are rendered outside React. When one of our modals opens on top of them the
 * user can no longer dismiss the prompt: Radix dialogs set `pointer-events: none` on
 * the body, so taps aimed at "Got it" / "Allow" land on our modal instead. Every
 * auto-opening modal reads this store and waits until the prompt is done.
 *
 * Ref counted so overlapping checks (e.g. a periodic GeoComply check during login)
 * only release the block once all of them finish.
 */

import { useSyncExternalStore } from 'react';

type Listener = () => void;

/**
 * Upper bound on how long a single prompt can block modals. Generous enough for a user
 * to read the explainer and answer the native prompt, but bounded so an SDK that never
 * fires its callbacks can't suppress the app's modals for the rest of the session.
 */
const MAX_PROMPT_DURATION_MS = 60_000;

class BrowserPromptStore {
  private activeCount = 0;

  private listeners = new Set<Listener>();

  getState = (): boolean => this.activeCount > 0;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private notify = (): void => {
    this.listeners.forEach((l) => l());
  };

  /**
   * Marks a browser prompt as on screen. Callers should invoke the returned release
   * function (in a `finally`) once the prompt is gone; it also releases itself after
   * MAX_PROMPT_DURATION_MS so a caller that never resolves can't block modals forever.
   */
  begin = (): (() => void) => {
    this.activeCount += 1;
    this.notify();

    let released = false;
    let safetyTimer: ReturnType<typeof setTimeout>;

    const release = () => {
      if (released) return;
      released = true;
      clearTimeout(safetyTimer);
      this.activeCount = Math.max(0, this.activeCount - 1);
      this.notify();
    };

    safetyTimer = setTimeout(release, MAX_PROMPT_DURATION_MS);

    return release;
  };
}

export const browserPromptStore = new BrowserPromptStore();

/**
 * Returns true while a browser permission prompt is on screen. Modals that open
 * without user action should render nothing while this is true.
 */
export function useIsBrowserPromptActive(): boolean {
  return useSyncExternalStore(
    browserPromptStore.subscribe,
    browserPromptStore.getState,
    () => false
  );
}
